import React, {useEffect} from 'react';
import {fontObserver} from '../../helper/font';
import sanity, {imageUrl} from '../../lib/sanity';
import Page from '../../components/page';
import Header from '../../components/header';
import {ContentSection} from '../../components/section';
import Content from '../../components/content';
import Slider from '../../components/slider';
import {H2} from '../../components/webComponents';
import Footer from '../../components/footer';
import Tabs from '../../components/tabs';
import Customers from '../../components/customers';
import SeoHeader from '../../components/seoHeader';

const usecaseQuery = (slug) => `*[_type == "usecase" && slug.current=="${slug}"] {
  title,
  slug,
  image,
  description,
  content,
  customers,
  "productUsecases": productUsecases[]{
    title,
    content,
    "images": images[]{
      asset,
      "height": asset->metadata.dimensions.height,
      "width": asset->metadata.dimensions.width,
    }
  }
}[0]`;

const usecasesQuery = `*[_type == "usecase"] | order(order asc, title asc) {
  slug
}`;

const productCategoriesQuery = `*[_type == "productCategory"] {
  _id,
  title,
  slug,
  image,
  "productAmount": count(*[_type == "product" && _id in ^.products[]._ref])
}`;

const News = ({usecase, productCategories}) => {
  useEffect(() => {
    fontObserver();
  }, []);
  return (
    <Page>
      <SeoHeader
        title={usecase?.title}
        description={usecase?.description}
        image={imageUrl(usecase?.image, 80, 600)}
        url={`/referenzen/${usecase?.slug?.current}`}
      />
      <Header
        title={usecase?.title}
        description={usecase?.description}
        placeholderImage={imageUrl(usecase?.image, 10, 10)}
        largeImage={imageUrl(usecase?.image, 80, 1000)}
      />
      <ContentSection>{usecase?.content && <Content content={usecase?.content} />}</ContentSection>
      {usecase?.customers?.length > 0 && (
        <ContentSection>
          <Customers
            items={usecase?.customers?.map((item) => ({
              name: item?.name,
              logo: imageUrl(item?.logo, 80, 200),
            }))}
          />
        </ContentSection>
      )}
      {usecase?.productUsecases?.length > 0 && (
        <ContentSection>
          <Tabs
            grid
            items={usecase?.productUsecases?.map((item) => ({
              title: item?.title,
              content: item?.content,
              images: item?.images,
            }))}
          />
        </ContentSection>
      )}
      {productCategories?.length > 0 && (
        <ContentSection>
          <H2>Produkt-Kategorien</H2>
          <Slider
            items={productCategories?.map((item) => ({
              line1: item?.title,
              line2: `${item?.productAmount} ${item?.productAmount === 1 ? `Produkt` : `Produkte`}`,
              image: imageUrl(item?.image, 80, 500),
              as: `/produkt-kategorie/${item?.slug?.current}`,
              link: `/produkt-kategorie/[slug]`,
            }))}
          />
        </ContentSection>
      )}
      <Footer />
    </Page>
  );
};

export const getStaticProps = async (context) => {
  const productCategories = await sanity.fetch(productCategoriesQuery);
  const usecase = await sanity.fetch(usecaseQuery(context.params?.slug?.toString()));
  if (!usecase) {
    return {props: {}, revalidate: 1, notFound: true};
  }
  return {
    props: {usecase, productCategories},
    revalidate: 10,
  };
};

export const getStaticPaths = async () => {
  const usecases = await sanity.fetch(usecasesQuery);
  return {
    paths: usecases?.map((item) => ({params: {slug: item?.slug?.current}})) || [],
    fallback: true,
  };
};

export default News;
